import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { X, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const ACCENT = "#F5B82E";
const DANGER = "#E53935";
const WARN = "#FFD166";

export default function DashboardDrillDown({ open, onClose, payload }) {
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !payload) return null;

  function goToModule() {
    if (payload.route) {
      navigate(payload.route.path, payload.route.state ? { state: payload.route.state } : undefined);
      onClose();
    }
  }

  return (
    <>
      <div
        className="drill-overlay"
        onClick={onClose}
        data-testid="drill-overlay"
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 60,
          animation: "fadeIn 0.2s ease",
        }}
      />
      <div
        className="drill-drawer"
        data-testid="drill-drawer"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(520px, 92vw)", background: "var(--bg-1)",
          borderLeft: "1px solid var(--border-2)", zIndex: 61,
          display: "flex", flexDirection: "column",
          animation: "slideInRight 0.25s ease",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.4)",
        }}
      >
        <header style={{
          padding: "18px 22px", borderBottom: "1px solid var(--border-2)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div className="text-xs text-dim" style={{ textTransform: "uppercase", letterSpacing: 1.2 }}>
              {payload.kind === "kpi" ? "Indicador clave" : "Indicador ambiental"}
            </div>
            <h2 className="card-h1" style={{ margin: "4px 0 0 0", fontSize: 18 }}>{payload.label}</h2>
            {payload.subtitle && <div className="text-xs text-dim mt-2">{payload.subtitle}</div>}
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="drill-close" title="Cerrar">
            <X size={14} />
          </button>
        </header>

        <div style={{ flex: 1, overflow: "auto", padding: 22 }}>
          {payload.kind === "indicator" ? (
            <IndicatorDetail ind={payload.indicator} />
          ) : (
            <KpiDetail kpiKey={payload.key} data={payload.kpiData} />
          )}
        </div>

        {payload.route && (
          <footer style={{ padding: 16, borderTop: "1px solid var(--border-2)" }}>
            <button
              className="btn btn-primary"
              onClick={goToModule}
              data-testid="drill-goto-module"
              style={{ width: "100%" }}
            >
              <ExternalLink size={14} /> Ir al módulo
            </button>
          </footer>
        )}
      </div>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
}

function IndicatorDetail({ ind }) {
  if (!ind) return null;
  const monthly = ind.monthly_full || ind.sparkline || [];
  const series = MONTHS.map((m, i) => ({
    month: m,
    value: Number(monthly[i] ?? 0),
  }));
  const total = series.reduce((s, r) => s + r.value, 0);
  const max = Math.max(...series.map((s) => s.value));
  const peakIdx = series.findIndex((s) => s.value === max);
  const peakMonth = max > 0 ? MONTHS[peakIdx] : "—";
  const nonZero = series.filter((r) => r.value > 0).length;

  return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatBox label="YTD" value={total.toLocaleString("es-MX", { maximumFractionDigits: 2 })} unit={ind.unit} />
        <StatBox label="Mes pico" value={peakMonth} unit={max > 0 ? max.toLocaleString("es-MX", { maximumFractionDigits: 2 }) : ""} />
        <StatBox label="Meses con dato" value={`${nonZero}/12`} unit="" />
      </div>

      <div className="card" style={{ background: "var(--bg-2)" }}>
        <div className="card-title">Distribución mensual Ene – Dic</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={series}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={10} interval={0} />
            <YAxis stroke="var(--text-dim)" fontSize={10} />
            <Tooltip
              contentStyle={{ background: "var(--bg-1)", border: "1px solid var(--border-2)", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [Number(v).toLocaleString("es-MX") + (ind.unit ? " " + ind.unit : ""), ind.label]}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {series.map((r, i) => (
                <Cell key={i} fill={i === peakIdx && max > 0 ? WARN : ACCENT} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card mt-3" style={{ background: "var(--bg-2)" }}>
        <div className="card-title">Detalle por mes</div>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {series.map((r) => (
            <div
              key={r.month}
              className="flex items-center justify-between p-2 rounded"
              style={{ background: "var(--bg-1)", fontSize: 12 }}
              data-testid={`drill-month-${r.month.toLowerCase()}`}
            >
              <span className="text-dim">{r.month}</span>
              <span className="font-bold">
                {r.value ? r.value.toLocaleString("es-MX", { maximumFractionDigits: 2 }) : "—"}
                {r.value && ind.unit ? <span className="text-dim ml-2" style={{ fontWeight: 400, fontSize: 10 }}> {ind.unit}</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function StatBox({ label, value, unit }) {
  return (
    <div className="p-3 rounded" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
      <div className="text-xs text-dim" style={{ textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: "var(--text)" }}>{value}</div>
      {unit && <div className="text-xs text-dim mt-2">{unit}</div>}
    </div>
  );
}

function KpiDetail({ kpiKey, data }) {
  const [extra, setExtra] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      try {
        let payload = {};
        if (kpiKey === "legal") {
          const r = await api.get("/data/legal_requirements", { params: { compliance_status: "No cumple", limit: 20 } });
          payload.items = (r.data || []).slice(0, 10);
        } else if (kpiKey === "racs") {
          const r = await api.get("/data/racs", { params: { status: "Abierta", limit: 20 } });
          payload.items = (r.data || []).slice(0, 10);
        } else if (kpiKey === "procedures") {
          payload.items = (data.proc_compliance || []).slice(0, 10);
        } else if (kpiKey === "studies") {
          const r = await api.get("/data/studies", { params: { status: "Vencido", limit: 20 } });
          payload.items = (r.data || []).slice(0, 10);
        }
        if (alive) setExtra(payload);
      } catch (e) { console.error(e); if (alive) setExtra({ items: [] }); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [kpiKey, data]);

  if (kpiKey === "ica") return <IcaBreakdown data={data} />;

  const titleMap = {
    legal: "Requisitos no cumplidos",
    racs: "RACs abiertas",
    procedures: "Procedimientos con menor avance",
    studies: "Estudios vencidos",
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {kpiKey === "legal" && (
          <>
            <StatBox label="Cumplimiento" value={`${data.kpis.legal.pct}%`} unit="" />
            <StatBox label="No cumplen" value={data.kpis.legal.critical} unit={`de ${data.kpis.legal.total} totales`} />
          </>
        )}
        {kpiKey === "racs" && (
          <>
            <StatBox label="Tasa de cierre" value={`${data.kpis.racs.closure_rate}%`} unit="" />
            <StatBox label="Abiertas" value={data.kpis.racs.open} unit={`${data.kpis.racs.closed} cerradas`} />
          </>
        )}
        {kpiKey === "procedures" && (
          <>
            <StatBox label="Promedio" value={`${data.kpis.procedures.avg_pct}%`} unit="" />
            <StatBox label="Bajos (<50%)" value={data.kpis.procedures.low} unit={`de ${data.kpis.procedures.total} totales`} />
          </>
        )}
        {kpiKey === "studies" && (
          <>
            <StatBox label="Vigentes" value={data.kpis.studies.vigentes} unit="" />
            <StatBox label="Vencidos" value={data.kpis.studies.vencidos} unit={`${data.kpis.studies.total} totales`} />
          </>
        )}
      </div>

      <div className="card" style={{ background: "var(--bg-2)" }}>
        <div className="card-title">{titleMap[kpiKey] || "Detalle"}</div>
        {loading && <div className="text-xs text-dim">Cargando…</div>}
        {!loading && extra && extra.items.length === 0 && (
          <div className="text-xs text-dim">Sin registros a destacar.</div>
        )}
        {!loading && extra && extra.items.length > 0 && (
          <div className="grid gap-2">
            {extra.items.map((item, i) => (
              <KpiRow key={i} kpiKey={kpiKey} item={item} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function KpiRow({ kpiKey, item }) {
  let title = item.name || item.description || item.title || item.code || "—";
  let meta = "";
  let pct = null;
  if (kpiKey === "legal") {
    meta = `${item.regulation || ""}${item.article ? " · Art. " + item.article : ""}`;
  } else if (kpiKey === "racs") {
    meta = `Severidad: ${item.severity || "N/A"} · Vence: ${item.due_date || "—"}`;
  } else if (kpiKey === "procedures") {
    title = item.name || item.code;
    meta = item.code ? `Código ${item.code}` : "";
    pct = item.pct;
  } else if (kpiKey === "studies") {
    meta = `Vigencia: ${item.expiry_date || item.expires_at || "—"}`;
  }
  return (
    <div className="flex items-center justify-between p-2 rounded" style={{ background: "var(--bg-1)" }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="text-sm font-semibold" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        {meta && <div className="text-xs text-dim mt-1">{meta}</div>}
      </div>
      {pct !== null && (
        <span className="badge" style={{
          background: pct >= 80 ? "rgba(29,158,117,0.2)" : pct >= 50 ? "rgba(241,162,42,0.2)" : "rgba(226,75,74,0.2)",
          color: pct >= 80 ? ACCENT : pct >= 50 ? WARN : DANGER,
          fontWeight: 600,
        }}>{pct}%</span>
      )}
    </div>
  );
}

function IcaBreakdown({ data }) {
  const sub = [
    { name: "Cumplimiento legal", weight: 35, value: data.kpis.legal.pct },
    { name: "Cierre de RACs", weight: 25, value: data.kpis.racs.closure_rate },
    { name: "Procedimientos", weight: 30, value: data.kpis.procedures.avg_pct },
    { name: "Estudios vigentes", weight: 10, value: data.kpis.studies.vencidos === 0 ? 100 : 60 },
  ];
  return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatBox label="ICA actual" value={data.ecs_score} unit="/ 100" />
        <StatBox label="Δ vs mes anterior" value={`${data.ecs_delta >= 0 ? "+" : ""}${data.ecs_delta} pts`} unit="" />
        <StatBox label="Sin incidencia" value={`${data.days_without_incident} d`} unit="" />
      </div>
      <div className="card" style={{ background: "var(--bg-2)" }}>
        <div className="card-title">Composición del ICA</div>
        <div className="grid gap-3">
          {sub.map((s) => (
            <div key={s.name} className="p-3 rounded" style={{ background: "var(--bg-1)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{s.name}</span>
                <span className="text-xs text-dim">peso {s.weight}%</span>
              </div>
              <div className="flex items-center gap-3">
                <div style={{ flex: 1, height: 8, background: "var(--bg-2)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min(100, Math.max(0, s.value))}%`,
                    background: s.value >= 80 ? ACCENT : s.value >= 60 ? WARN : DANGER,
                    transition: "width 0.4s ease",
                  }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, minWidth: 60, textAlign: "right" }}>
                  {Math.round(s.value)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
